import { SvgArrowLeft } from '@cocoonspace/icons/react/font-awesome/regular/arrow-left'
import type { Event } from '@cocoonspace/sdk-js/types/event'
import type { Space } from '@cocoonspace/sdk-js/types/space'
import { authEmitter } from '@cocoonspace/shared/domains/auth/emitters/auth.emitter'
import { useAuth } from '@cocoonspace/shared/domains/auth/hooks/use-auth.hook'
import { useAuthDialogStore } from '@cocoonspace/shared/domains/auth/stores/use-auth-dialog-store.hook'
import { useEmitter } from '@cocoonspace/shared/hooks/use-emitter.hook'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { Separator } from '~/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs'
import DetailsSidebarMultidateBooking from '~/domains/booking/components/organisms/details-sidebar-multidate-booking'
import { DetailsSidebarStepMonth } from '~/domains/booking/components/organisms/details-sidebar-step-month'
import { DetailsSidebarStepStart } from '~/domains/booking/components/organisms/details-sidebar-step-start'
import { DetailsSidebarStepTime } from '~/domains/booking/components/organisms/details-sidebar-step-time'
import {
	type Direction,
	useStepper,
} from '~/domains/booking/hooks/use-stepper.hook'

export type BookingSteps = 'start' | 'monthSelect' | 'timeSelect'
export type TimeSlot = Pick<Event, 'start' | 'end' | 'day'>
export type BookingMode = 'create' | 'update' | 'visit'
export type BookingType = 'meeting' | 'visit' | 'quotation' | 'desk'

export interface BookingParams {
	bookingOption: TimeSlot
	slug: Space['id']
	visit: string
	event?: Event
}

interface BookingStepperProps {
	space: Space
	defaultValue: TimeSlot
	mode?: BookingMode
	type: BookingType
	visibleSteps?: BookingSteps[]
	activeStep?: BookingSteps
	event?: Event
	activeTab?: string
	onActiveTabChange?: (tab: string) => void
	onChange?: (props: {
		activeStep: BookingSteps
		bookingOption: TimeSlot
		direction: Direction
	}) => void
	onSuccess?: (params: BookingParams) => void
	onChangeType?: (type: BookingType) => void
}

const animation = {
	enter: (direction: string) => ({
		zIndex: 0,
		x: direction === 'next' ? 500 : -500,
		opacity: 0,
	}),
	center: { zIndex: 1, opacity: 1, x: 0 },
	exit: (direction: string) => ({
		zIndex: 0,
		x: direction === 'next' ? -500 : 500,
		opacity: 0,
	}),
}

const framerDivConfig = {
	variants: animation,
	initial: 'enter',
	animate: 'center',
	transition: {
		x: { type: 'spring', stiffness: 300, damping: 30 },
		opacity: { duration: 0.2 },
	},
}

const maxDate = dateTimeUtils().add(1, 'year').format('YYYY-MM-DD')

export const BookingStepper = ({
	defaultValue,
	visibleSteps,
	activeStep,
	space,
	mode = 'create',
	type,
	event,
	activeTab,
	onActiveTabChange,
	onChange,
	onSuccess,
}: BookingStepperProps) => {
	const { t } = useTranslation()
	const router = useRouter()
	const { isAuth } = useAuth()
	const openDialog = useAuthDialogStore((state) => state.openDialog)

	const { direction, steps, goBack, goForward, stepIndex } = useStepper(
		visibleSteps,
		activeStep,
	)

	const [bookingOption, setBookingOption] = useState<TimeSlot>(defaultValue)

	useEffect(() => {
		onChange?.({
			bookingOption,
			activeStep: steps[stepIndex],
			direction,
		})
	}, [bookingOption, stepIndex, onChange, steps]) // eslint-disable-line react-hooks/exhaustive-deps

	const onSubmit = () => {
		if (space.status !== 'published') return
		if (!bookingOption.day && !bookingOption.start && !bookingOption.end) return

		if (!isAuth) {
			router.push(
				{
					query: {
						...router.query,
						day: bookingOption.day,
						start: bookingOption.start?.replace(':', '-'),
						end: bookingOption.end?.replace(':', '-'),
					},
				},
				undefined,
				{ shallow: true },
			)

			openDialog({
				activeTab: 'register',
				errorMsg: t('errors:mustBeAuthToBook'),
			})

			return
		}

		onSuccess?.({
			// @ts-ignore
			event: {},
			bookingOption: {
				...bookingOption,
				start: bookingOption.start?.replace(':', '-'),
				end: bookingOption.end?.replace(':', '-'),
			},
			slug: space.slug,
		})
	}

	useEmitter(authEmitter, {
		'login.success': onSubmit,
		'register.success': onSubmit,
	})

	return (
		<div className='relative'>
			{stepIndex > 0 && (
				<div className='absolute inset-x-0 top-0 z-20 flex h-[60px] flex-row items-center px-6'>
					<button
						type='button'
						onClick={goBack}
					>
						<SvgArrowLeft className='size-5 text-black' />
					</button>
				</div>
			)}

			<div className='relative'>
				<AnimatePresence
					initial={false}
					custom={direction}
					mode='wait'
				>
					{steps[stepIndex] === 'start' && (
						<motion.div
							key='start'
							{...framerDivConfig}
							custom={direction}
						>
							{type === 'meeting' ? (
								<Tabs
									value={activeTab}
									onValueChange={onActiveTabChange}
								>
									<TabsList
										variant='material'
										className='w-full'
									>
										<TabsTrigger
											variant='material'
											value='daily'
											className='w-1/2'
										>
											{t('booking:stepper.oneTime.title')}
										</TabsTrigger>

										<TabsTrigger
											variant='material'
											value='multidate'
											className='w-1/2'
										>
											{t('space:sections.prices.pricings.multidate.title')}
										</TabsTrigger>
									</TabsList>

									<Separator />

									<TabsContent value='daily'>
										<DetailsSidebarStepStart
											space={space}
											className='px-4 py-2.5'
											booking={bookingOption}
											resetBooking={() =>
												setBookingOption({
													day: '',
													start: '08:00',
													end: '09:00',
												})
											}
											onClick={goForward}
										/>
									</TabsContent>

									<TabsContent value='multidate'>
										<DetailsSidebarMultidateBooking
											space={space}
											maxDate={maxDate}
										/>
									</TabsContent>
								</Tabs>
							) : (
								<DetailsSidebarStepStart
									space={space}
									booking={bookingOption}
									resetBooking={() =>
										setBookingOption({
											day: '',
											start: '08:00',
											end: '09:00',
										})
									}
									onClick={goForward}
								/>
							)}
						</motion.div>
					)}

					{steps[stepIndex] === 'monthSelect' && (
						<motion.div
							key='monthSelect'
							{...framerDivConfig}
							custom={direction}
						>
							<DetailsSidebarStepMonth
								selectedDay={bookingOption.day}
								maxDate={maxDate}
								spaceId={space.id}
								showPrices
								onDaySelect={(day) => {
									setBookingOption((booking) => ({ ...booking, day }))
									goForward()
								}}
							/>
						</motion.div>
					)}

					{steps[stepIndex] === 'timeSelect' && (
						<motion.div
							key='timeSelect'
							{...framerDivConfig}
							custom={direction}
						>
							<DetailsSidebarStepTime
								space={space}
								mode={mode}
								type={type}
								event={event}
								maxDate={maxDate}
								value={bookingOption}
								onChange={setBookingOption}
								onSubmit={onSubmit}
							/>
						</motion.div>
					)}
				</AnimatePresence>
			</div>
		</div>
	)
}
